import { Box, Chip } from '@mui/material';
import { DataCellProps } from '../types';

export type TeamStatusType = 'active' | 'deactivated' | 'scheduled' | 'deleted';

export interface TeamStatus {
  label: string;
  type: TeamStatusType;
}

export const getTeamStatus = (team: {
  dataDeletionExecutedOnDate?: string | null;
  archivedAt?: string | null;
  scheduledArchiveDate?: string | null;
}): TeamStatus => {
  if (team.dataDeletionExecutedOnDate) return { label: 'Deleted', type: 'deleted' };
  if (team.archivedAt) return { label: 'Deactivated', type: 'deactivated' };
  if (!team.archivedAt && team.scheduledArchiveDate)
    return {
      label: 'Scheduled for Deactivation',
      type: 'scheduled',
    };
  return { label: 'Active', type: 'active' };
};

// Alias for backward compatibility with existing code
const getStatus = (team: DataCellProps['team']) => getTeamStatus(team);

/**
 * TeamStatusBadge - Reusable badge component for displaying team status
 * Use this component anywhere you need to display a team status badge with consistent styling.
 *
 * @example
 * <TeamStatusBadge status={teamStatus} />
 * <TeamStatusBadge status={teamStatus} size="medium" />
 */
export const TeamStatusBadge = ({
  status,
  size = 'small',
}: {
  status: TeamStatus;
  size?: 'small' | 'medium';
}) => {
  const { label, type } = status;

  if (type === 'deactivated') {
    return (
      <Chip
        label={label}
        size={size}
        color="warning"
        sx={{
          color: '#ffffff',
          fontWeight: 500,
        }}
      />
    );
  }
  if (type === 'deleted') {
    return <Chip label={label} size={size} />;
  }
  if (type === 'scheduled') {
    return <Chip label={label} size={size} style={{ backgroundColor: '#FFC107', color: '#000' }} />;
  }
  // active
  return <Chip label={label} color="primary" size={size} />;
};

/**
 * SsoBadge - Badge component for displaying SSO enabled status
 * Only renders when ssoEnabled is true
 *
 * @example
 * {ssoEnabled && <SsoBadge size="medium" />}
 */
export const SsoBadge = ({ size = 'small' }: { size?: 'small' | 'medium' }) => {
  return (
    <Chip
      label="SSO Enabled"
      size={size}
      color="info"
      sx={{
        fontWeight: 500,
      }}
    />
  );
};

export const TeamsV2StatusCell = ({ team }: DataCellProps) => {
  const status = getStatus(team);
  return (
    <Box>
      <TeamStatusBadge status={status} />
    </Box>
  );
};

export default TeamsV2StatusCell;
